// ============================================================================
// ENUMS
// ============================================================================

export enum TemperatureScope {
  // Single Operation Mode: Show one operation with all radial points (annuli/pipes)
  SingleOperation = 'SingleOperation',
  // Multiple Operations Mode: Show all operations for one radial point (annulus/pipe)
  MultipleOperations = 'MultipleOperations'
}

export enum PressureScope {
  // Single Operation Mode: Show one operation with all radial points (annuli/pipes)
  SingleOperation = 'SingleOperation',
  // Multiple Operations Mode: Show all operations for one radial point (annulus/pipe)
  MultipleOperations = 'MultipleOperations'
}

export enum AnnularResultsScope {
  // Single Result Mode: Show one operation with ONE property across ALL annuli
  SingleResult = 'SingleResult',
  // Single Annulus Mode: Show one operation with ONE annulus showing ALL properties
  SingleAnnulus = 'SingleAnnulus'
}

export enum OperationEnvelopeScope {
  SingleOperation = 'SingleOperation',
  MultipleOperations = 'MultipleOperations'
}

export enum OperationEnvelopeTableOutput {
  // Show operation profile data (Temperature-Pressure-Depth points from wellhead to perforation)
  OperationProfile = 'OperationProfile',
  // Show phase envelope data (Temperature-Pressure boundary curve for the fluid)
  PhaseEnvelope = 'PhaseEnvelope',
  // Show both operation profile and phase envelope data
  Both = 'Both'
}

// ============================================================================
// CHART CONFIGURATION INTERFACES
// ============================================================================

export interface TemperatureChartConfig {
  scope: TemperatureScope;
  operationName?: string;
  operationNames?: string[];
  annulusOrPipeName?: string;
  annulusOrPipeNames?: string[];
  tableColumns?: string[];
}

export interface PressureChartConfig {
  scope: PressureScope;
  operationName?: string;
  operationNames?: string[];
  annulusOrPipeName?: string;
  annulusOrPipeNames?: string[];
  tableColumns?: string[];
}

export interface AnnularResultsChartConfig {
  scope: AnnularResultsScope;
  operationName?: string;
  propertyName?: string;
  annulusName?: string;
  fluidOrGasFractions?: string;
  selectedProperties?: string[];
  tableColumns?: string[];
}

export interface OperationEnvelopeChartConfig {
  scope: OperationEnvelopeScope;
  operationName?: string;  // When Scope = SingleOperation, which operation to show (null = all operations)
  fluidName?: string;  // When Scope = MultipleOperations, which fluid/phase envelope to show (null = all fluids)
  tableOutput?: OperationEnvelopeTableOutput;  // When table is requested for single operation mode, what data to include
}

// ============================================================================
// REPORT OPTIONS INTERFACE
// ============================================================================

/**
 * Configuration options for what to include in a Perical Results Report
 * Supports Temperatures, Pressures, Annular Results, and Operation Envelope charts/tables
 */
export interface PericalReportOptions {
  // Temperature chart/table options
  includeTemperaturePlot?: boolean;
  includeTemperatureTable?: boolean;
  temperatureChartConfig?: TemperatureChartConfig;

  // Pressure chart/table options
  includePressurePlot?: boolean;
  includePressureTable?: boolean;
  pressureChartConfig?: PressureChartConfig;

  // Annular Results options
  // SingleResult scope = Chart (one chart per annulus for selected property)
  // SingleAnnulus scope = Table (all properties for selected annulus)
  includeAnnularResults?: boolean;
  annularResultsChartConfig?: AnnularResultsChartConfig;

  // Operation Envelope chart/table options
  includeOperationEnvelopePlot?: boolean;
  includeOperationEnvelopeTable?: boolean;
  operationEnvelopeChartConfig?: OperationEnvelopeChartConfig;

  // Formations option
  includeFormations?: boolean;

  // Temperature table formation columns option
  includeFormationColumnsInTemperatureTable?: boolean;
}

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_CHART_CONFIGS = {
  temperature: {
    scope: TemperatureScope.SingleOperation,
    operationName: null,
    operationNames: [],
    annulusOrPipeName: null,
    annulusOrPipeNames: [],
    tableColumns: []
  } as TemperatureChartConfig,

  pressure: {
    scope: PressureScope.SingleOperation,
    operationName: null,
    operationNames: [],
    annulusOrPipeName: null,
    annulusOrPipeNames: [],
    tableColumns: []
  } as PressureChartConfig,

  annularResults: {
    scope: AnnularResultsScope.SingleResult,
    operationName: null,
    propertyName: null,
    annulusName: null,
    fluidOrGasFractions: 'Fluid',
    selectedProperties: [],
    tableColumns: []
  } as AnnularResultsChartConfig,

  operationEnvelope: {
    scope: OperationEnvelopeScope.SingleOperation,
    operationName: null,
    fluidName: null,
    tableOutput: OperationEnvelopeTableOutput.Both
  } as OperationEnvelopeChartConfig
};

// ============================================================================
// PREDEFINED REPORT CONFIGURATIONS
// ============================================================================

/**
 * Predefined report configurations
 */
export class PericalReportPresets {
  /**
   * No selections (used for first-time setup)
   */
  static readonly None: PericalReportOptions = {
    includeTemperaturePlot: false,
    includeTemperatureTable: false,
    includePressurePlot: false,
    includePressureTable: false,
    includeAnnularResults: false,
    includeOperationEnvelopePlot: false,
    includeOperationEnvelopeTable: false,
    includeFormations: false,
    includeFormationColumnsInTemperatureTable: false,
    temperatureChartConfig: DEFAULT_CHART_CONFIGS.temperature,
    pressureChartConfig: DEFAULT_CHART_CONFIGS.pressure,
    annularResultsChartConfig: DEFAULT_CHART_CONFIGS.annularResults,
    operationEnvelopeChartConfig: DEFAULT_CHART_CONFIGS.operationEnvelope
  };

  /**
   * Default configuration with all options enabled
   */
  static readonly Default: PericalReportOptions = {
    includeTemperaturePlot: true,
    includeTemperatureTable: true,
    includePressurePlot: true,
    includePressureTable: true,
    includeAnnularResults: true,
    includeOperationEnvelopePlot: true,
    includeOperationEnvelopeTable: true,
    includeFormations: true,
    includeFormationColumnsInTemperatureTable: true,
    temperatureChartConfig: DEFAULT_CHART_CONFIGS.temperature,
    pressureChartConfig: DEFAULT_CHART_CONFIGS.pressure,
    annularResultsChartConfig: DEFAULT_CHART_CONFIGS.annularResults,
    operationEnvelopeChartConfig: DEFAULT_CHART_CONFIGS.operationEnvelope
  };

  /**
   * Minimal configuration with only essential charts
   */
  static readonly Minimal: PericalReportOptions = {
    includeTemperaturePlot: true,
    includeTemperatureTable: false,
    includePressurePlot: true,
    includePressureTable: false,
    includeAnnularResults: false,
    includeOperationEnvelopePlot: false,
    includeOperationEnvelopeTable: false,
    includeFormations: false,
    includeFormationColumnsInTemperatureTable: false,
    temperatureChartConfig: DEFAULT_CHART_CONFIGS.temperature,
    pressureChartConfig: DEFAULT_CHART_CONFIGS.pressure,
    annularResultsChartConfig: DEFAULT_CHART_CONFIGS.annularResults,
    operationEnvelopeChartConfig: DEFAULT_CHART_CONFIGS.operationEnvelope
  };

  /**
   * Charts-only configuration (no tables)
   */
  static readonly ChartsOnly: PericalReportOptions = {
    includeTemperaturePlot: true,
    includeTemperatureTable: false,
    includePressurePlot: true,
    includePressureTable: false,
    includeAnnularResults: true,
    includeOperationEnvelopePlot: false,
    includeOperationEnvelopeTable: false,
    includeFormations: false,
    includeFormationColumnsInTemperatureTable: false,
    temperatureChartConfig: DEFAULT_CHART_CONFIGS.temperature,
    pressureChartConfig: DEFAULT_CHART_CONFIGS.pressure,
    annularResultsChartConfig: DEFAULT_CHART_CONFIGS.annularResults,
    operationEnvelopeChartConfig: DEFAULT_CHART_CONFIGS.operationEnvelope
  };

  /**
   * Tables-only configuration (no charts)
   */
  static readonly TablesOnly: PericalReportOptions = {
    includeTemperaturePlot: false,
    includeTemperatureTable: true,
    includePressurePlot: false,
    includePressureTable: true,
    includeAnnularResults: true,
    includeOperationEnvelopePlot: false,
    includeOperationEnvelopeTable: false,
    includeFormations: false,
    includeFormationColumnsInTemperatureTable: false,
    temperatureChartConfig: DEFAULT_CHART_CONFIGS.temperature,
    pressureChartConfig: DEFAULT_CHART_CONFIGS.pressure,
    annularResultsChartConfig: DEFAULT_CHART_CONFIGS.annularResults,
    operationEnvelopeChartConfig: DEFAULT_CHART_CONFIGS.operationEnvelope
  };
}

/**
 * Helper to validate Perical report options
 */
export function validateReportOptions(options: PericalReportOptions): string[] {
  const errors: string[] = [];

  // Check if at least one option is selected
  const hasAnySelection = 
    options.includeTemperaturePlot ||
    options.includeTemperatureTable || options.includeFormationColumnsInTemperatureTable ||
    options.includePressurePlot ||
    options.includePressureTable ||
    options.includeAnnularResults ||
    options.includeOperationEnvelopePlot ||
    options.includeOperationEnvelopeTable;

  if (!hasAnySelection) {
    errors.push('At least one chart or table must be selected');
  }

  return errors;
}

